import type { ReactNode } from "react";

import { EmptyState } from "@/design-system/feedback/EmptyState";

export type DataTableColumn<T> = {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
};

type Props<T> = {
  data: T[];
  columns: DataTableColumn<T>[];
  emptyTitle?: string;
  emptyDescription?: string;
};

export function DataTable<T>({
  data,
  columns,
  emptyTitle = "No records found",
  emptyDescription = "Try adjusting your search or filters.",
}: Props<T>) {
  if (data.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/80 shadow-sm shadow-black/20">
      <table className="min-w-full">
        <thead className="border-b border-slate-800 bg-slate-950/70">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="group border-b border-slate-800 transition-all duration-200 last:border-b-0 hover:bg-slate-900/70"
            >
              {columns.map((column, columnIndex) => (
                <td
                  key={column.key}
                  className={`px-5 py-5 text-sm ${
                    columnIndex === 0
                      ? "font-medium text-white"
                      : "text-slate-300"
                  }`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
